import ModalContentWithCloseButton from "../common/ModalContentWithCloseButton";
import {
  AddCollaboratorForm,
  ButtonsRow,
  CollaboratorItem,
  CollaboratorsContainer,
  CollaboratorsList,
  Description,
  Title,
} from "./ShareBoardStyles";
import React, { useEffect } from "react";
import Button from "../common/Button";
import * as Yup from "yup";
import { EMAIL_SCHEMA } from "../../utils/forms";
import { Formik } from "formik";
import { ButtonTypes } from "../common/Button/constants";
import FormikFormLine from "../formik-form-components/FormikFormLine";
import { FieldTypes } from "../formik-form-components/FormikFormLine/constants";
import CircleBadge from "../common/CircleBadge";
import Modal from "../common/Modal";
import { UserSummary } from "../../reducers/models/users";
import useBoardCollaborators, {
  AddCollaboratorFormValues,
} from "../../utils/hooks/commonly-stored/uploaded-board/collaborators";
import LoadingOverlay from "../LoadingOverlay";
import useChangeBoardOwner from "../../utils/hooks/commonly-stored/uploaded-board/changeBoardOwner";
import { useRefreshBoardInStore } from "../../utils/hooks/commonly-stored/uploaded-board/boardRefreshes";
import { useCustomAlert } from "../../utils/hooks/alerts";
import { useAppSelector } from "../../utils/hooks/store";

const schema = Yup.object().shape({
  email: EMAIL_SCHEMA,
});

const initialValues: AddCollaboratorFormValues = {
  email: "",
};

function AddCollaborator({ boardId }: { boardId: string }) {
  const isAdminOrEngineer = useAppSelector(
    (state) => state.auth.isAdminOrEngineer,
  );
  const { addCollaborator, updating } = useBoardCollaborators(boardId);
  const { refreshBoard } = useRefreshBoardInStore();
  const customAlert = useCustomAlert();

  const onErrorChangingOwner = () => {
    customAlert("Failed to change owner");
  };

  const { changeBoardOwner, changingOwner } = useChangeBoardOwner(
    onErrorChangingOwner,
    refreshBoard,
  );

  const handleChangeBoardOwner = (newOwnerEmail: string) => {
    changeBoardOwner(newOwnerEmail, boardId);
  };

  return (
    <>
      <LoadingOverlay
        loading={changingOwner}
        text={"Updating board owner..."}
      />
      <LoadingOverlay loading={updating} text={"Adding collaborator..."} />
      <Formik
        initialValues={initialValues}
        onSubmit={addCollaborator}
        validationSchema={schema}
      >
        {({ handleSubmit, isValid, values }) => {
          return (
            <AddCollaboratorForm onSubmit={handleSubmit}>
              <FormikFormLine
                name="email"
                label="Email"
                type={FieldTypes.email}
              />
              <Description>
                Collaborators can edit the project details, bill of materials,
                and quote, and can purchase the board.
              </Description>
              <ButtonsRow>
                {isAdminOrEngineer && (
                  <Button
                    disabled={!isValid || !values.email}
                    type={ButtonTypes.BUTTON}
                    centerText
                    label={"Change owner"}
                    onClick={() => {
                      handleChangeBoardOwner(values.email);
                    }}
                  />
                )}
                <Button
                  disabled={!isValid}
                  type={ButtonTypes.SUBMIT}
                  leftIconProps={{
                    src: isValid ? "/icons/plus_white.svg" : "/icons/plus.svg",
                    alt: "Add collaborator",
                  }}
                  label={"Add collaborator"}
                />
              </ButtonsRow>
            </AddCollaboratorForm>
          );
        }}
      </Formik>
    </>
  );
}

function RemoveCollaborator({
  boardId,
  collaborator,
}: {
  boardId: string;
  collaborator: UserSummary;
}) {
  const { removeCollaborator, updating } = useBoardCollaborators(boardId);

  return (
    <>
      <LoadingOverlay loading={updating} text={"Removing collaborator..."} />
      <CollaboratorItem>
        {collaborator.email}
        <CircleBadge
          iconProps={{
            src: "/icons/remove.svg",
            alt: "cancel",
          }}
          onClick={() => removeCollaborator(collaborator)}
        />
      </CollaboratorItem>
    </>
  );
}

function CollaboratorList({ boardId }: { boardId: string }) {
  const { collaborators } = useBoardCollaborators(boardId);

  if (collaborators === null || collaborators.length == 0) {
    return null;
  }

  return (
    <CollaboratorsContainer>
      <Title>Collaborators</Title>
      <CollaboratorsList>
        {collaborators.map((collaborator) => {
          return (
            <RemoveCollaborator
              key={collaborator.id}
              boardId={boardId}
              collaborator={collaborator}
            />
          );
        })}
      </CollaboratorsList>
    </CollaboratorsContainer>
  );
}

export default function ShareBoard({
  shareBoardId,
  setShareBoardId,
}: {
  shareBoardId: string | null;
  setShareBoardId: (boardId: string | null) => void;
}) {
  const dismissShareModal = () => {
    setShareBoardId(null);
  };

  useEffect(() => {
    if (shareBoardId === null) {
      document.body.style.overflow = "auto";
    } else {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [shareBoardId]);

  return (
    <>
      <Modal isOpen={shareBoardId !== null} onRequestClose={dismissShareModal}>
        <ModalContentWithCloseButton
          title="Share board"
          closeModal={dismissShareModal}
        >
          {shareBoardId !== null && (
            <>
              <AddCollaborator boardId={shareBoardId} />
              <CollaboratorList boardId={shareBoardId} />
            </>
          )}
        </ModalContentWithCloseButton>
      </Modal>
    </>
  );
}
